import { Typography } from '@mui/material'
import { Link } from 'react-router-dom'
import {
  textOperators,
  equalityOperators,
  multiselectOperators,
  dateOperators
} from '../../../utils/constants/operators'
import { buildStatusColumn } from '../../fields/StatusField'
import { activityTypes } from '../../../utils/constants/misc'

export const getQuoteColumns = ({ statusList = [], customers = [] }) => {
  const getCustomersFilter = () =>
    customers.map((item) => ({
      name: item.id.toString(),
      label: item.customer_name
    }))

  return [
    {
      name: 'quote_number',
      label: 'N° Devis',
      isFilter: true,
      operators: textOperators
    },
    {
      name: 'customer_id',
      label: 'Client',
      isFilter: true,
      valueEditorType: 'multiselect',
      values: getCustomersFilter(),
      mapping: (id) => {
        const result = customers.find((item) => item.id === id)
        if (result)
          return (
            <Typography state={{ id: id, history: '/invoices' }} component={Link} to={`/customers/view`}>
              {result.customer_name}
            </Typography>
          )
        else return id
      },
      operators: [...equalityOperators, ...multiselectOperators]
    },
    buildStatusColumn('quotes', statusList),
    {
      name: 'summary',
      label: 'Titre',
      isFilter: false
    },
    {
      name: 'quote_date',
      label: 'Date Devis',
      isFilter: true,
      inputType: 'date',
      type: 'date',
      format: 'DD/MM/YYYY',
      operators: dateOperators
    },
    { name: 'total_amount_net', label: 'Montant total', type: 'currency', isFilter: false },
    {
      name: 'createdDateTime',
      label: 'Date Création',
      isFilter: true,
      inputType: 'datetime-local',
      type: 'date',
      format: 'DD/MM/YYYY HH:mm:ss',
      operators: dateOperators
    },
    {
      name: 'createdBy',
      label: 'Créé Par',
      isFilter: true,
      operators: [...equalityOperators, ...multiselectOperators]
    },
    {
      name: 'lastModDateTime',
      label: 'Date Modification',
      isFilter: true,
      inputType: 'datetime-local',
      type: 'date',
      format: 'DD/MM/YYYY HH:mm:ss',
      operators: dateOperators
    },
    {
      name: 'lastModBy',
      label: 'Modifié Par',
      isFilter: true,
      operators: [...equalityOperators, ...multiselectOperators]
    }
  ]
}

export const getQuoteFields = (user, location, setQuoteLines) => [
  [
    {
      name: 'customer_id',
      label: 'Client',
      required: true,
      type: 'select',
      defaultValue: () => location?.state?.context?.customer_id ?? null,
      options: {
        type: 'api',
        object: 'customers',
        value: 'customer_name',
        returnField: 'id',
        returnFields: ['id'],
        filter: () => ({ owner_id: user.company_id })
      },
      sizeDivided: 2,
      fullWidth: true,
      validation: [
        {
          rule: 'req',
          error: 'Client obligatoire'
        }
      ]
    }
  ],
  [
    {
      name: 'quote_number',
      label: 'N° Devis',
      readOnly: true,
      required: true,
      type: 'text',
      fullWidth: true,
      validationWhen: () => location.pathname === '/quotes/edit',
      validation: [
        {
          rule: 'req',
          error: 'N° Devis obligatoire'
        }
      ]
    },
    {
      name: 'quote_date',
      label: 'Date',
      required: true,
      type: 'date',
      fullWidth: true,
      validation: [
        {
          rule: 'req',
          error: 'Date obligatoire'
        }
      ]
    },
    {
      name: 'validity_date',
      label: 'Date validité',
      type: 'date',
      required: true,
      fullWidth: true,
      readOnly: true
    }
  ],
  [
    {
      name: 'total_amount_net',
      label: 'Montant Total',
      type: 'currency',
      fullWidth: true,
      readOnly: true,
      sizeDivided: 4
    }
  ],
  [
    {
      name: 'summary',
      label: 'Titre',
      type: 'text',
      fullWidth: true,
      sizeDivided: 2
    }
  ],
  [
    {
      name: 'description',
      label: 'Description',
      type: 'text-multiline',
      fullWidth: true,
      sizeDivided: 2
    }
  ],
  [
    {
      name: 'lines',
      label: 'Lignes',
      type: 'mytable',
      tableOptions: {
        paperStyle: { width: '100%', margin: '0 auto' },
        setDataParent: setQuoteLines,
        defaultSorted: 'id',
        columns: [
          {
            name: 'id',
            display: false,
            enableEditing: false
          },
          {
            name: 'description',
            label: 'Description',
            type: 'freeselect',
            onChangeF: (data, priceBook, onRowDataChange) => {
              const dataN = { ...data }
              if (dataN.priceBook_id !== undefined) {
                const p = priceBook.find((p) => p.id === data.priceBook_id)
                if (p) {
                  dataN.unit_price = p.unit_price
                  dataN.quantity = p.quantity
                  dataN.net_amount = p.unit_price * p.quantity
                  dataN.type = p.type
                  dataN.priceBook_id = p.id
                }
              }
              onRowDataChange(dataN)
            },
            optionsAPI: {
              api: 'priceBooks',
              filter: () => ({ company_id: user.company_id })
            },
            dataId: () => 'priceBook_id',
            enableEditing: true,
            size: 80,
            validation: [
              {
                rule: 'req',
                error: 'Description obligatoire'
              }
            ]
          },
          {
            name: 'quantity',
            label: 'Quantité',
            enableEditing: true,
            type: 'float',
            min: 0,
            validation: [
              {
                rule: 'req',
                error: 'Quantité obligatoire'
              }
            ]
          },
          {
            name: 'unit_price',
            label: 'Prix unitaire',
            type: 'currency',
            enableEditing: true
          },

          {
            name: 'net_amount',
            label: 'Montant',
            type: 'currency',
            enableEditing: false,
            defaultValueFunction: (props) => {
              const val = parseFloat(props.quantity) * parseFloat(props.unit_price)
              return isNaN(val) ? undefined : val
            }
          },
          {
            label: 'Type',
            name: 'type',
            type: 'select',
            enableEditing: true,
            mapping: (val) => {
              return activityTypes.find((i) => i.id === val)?.label ?? val
            },
            options: {
              type: 'data',
              data: activityTypes,
              returnField: 'id',
              returnFields: ['id'],
              value: 'label'
            },
            validation: [
              {
                rule: 'req',
                error: 'Type obligatoire'
              }
            ]
          }
        ],
        actions: {
          add: true,
          edit: true,
          editDisabled: (quote) => quote.status >= 70,
          delete: {
            message: 'Voulez-vous vraiment supprimer la ligne'
          }
        },
        title: 'Lignes',
        options: {
          pageSize: 10,
          search: false,
          paging: false,
          columnsButton: true,
          actionsColumnIndex: -1
        }
      },
      validation: [
        {
          rule: 'req',
          error: 'Saisir au moins 1 ligne'
        }
      ]
    }
  ]
]
